
import VCurrencyField from "@/components/forms/VCurrencyField.vue";
import { Component, Model, Prop, Vue, Watch } from "vue-property-decorator";

/**
 * Price input with a day / course unit switch inside the field.
 *
 * The entered amount (`value`) is the source of truth and is never rewritten by
 * the component; `priceType` only records how to read it — 0 = per day,
 * 1 = per whole course. Switching the unit toggles `priceType`, the amount
 * stays as typed; the "Total" hint is derived from it for per-day pricing.
 */
@Component({
  components: { VCurrencyField }
})
export default class CoursePriceField extends Vue {
  // The price is exposed through v-model (value/input) so a wrapping
  // vee-validate ValidationProvider can observe it. priceType stays a .sync prop.
  @Model("input", { type: Number, default: 0 }) value!: number;
  @Prop({ default: 1 }) priceType!: number;
  @Prop({ default: 0 }) durationDays!: number;
  @Prop({ required: false }) errors!: any;

  get mode(): "perDay" | "total" {
    return this.priceType == 0 ? "perDay" : "total";
  }

  get displayValue(): number {
    const num = Number(this.value) || 0;
    // Ensure we always return a proper number, not a string
    return Number(num);
  }

  get perDayValue(): number {
    const num = Number(this.value) || 0;
    // Ensure we always return a proper number, not a string
    return Number(num);
  }

  get totalValue(): number {
    const num = this.mode === "total"
      ? Number(this.value) || 0
      : this.perDayValue * this.durationDays;
    // Ensure we always return a proper number, not a string
    return Number(num);
  }

  get hint(): string {
    let hintMessage = "";

    if (this.priceType == 0) {
      hintMessage = `Total: ${this.formatMoney(this.totalValue)} for ${
        this.durationDays
      } days`;
    }

    return hintMessage;
  }

  onPriceInput(value: number) {
    const amount = Number(value) || 0;
    if (this.mode === "total") {
      this.$emit("update:priceType", 1);
      if (this.durationDays) {
        this.$emit("input", amount);
      }
    } else {
      this.$emit("input", amount);
    }
  }

  onModeChange(mode: "perDay" | "total") {
    if (mode === this.mode) return;

    if (mode == "total") {
      this.$emit("update:priceType", 1);
    } else {
      this.$emit("update:priceType", 0);
    }
  }

  @Watch("durationDays")
  onDurationDaysChange() {
    if (this.mode !== "total") {
      return;
    }
    if (this.durationDays) {
      this.$emit(
        "update:price",
        this.round(Number(this.value) / this.durationDays)
      );
    }
  }

  round(value: number): number {
    return Math.round(value * 100) / 100;
  }

  formatMoney(value: number): string {
    return new Intl.NumberFormat("en", {
      style: "currency",
      currency: "ZAR",
      minimumFractionDigits: 2
    })
      .format(value)
      .substr(2);
  }
}
